import {useCallback} from 'react';

import {replace} from 'ramda';

import {ParsePersonalIdApiArg, useParsePersonalIdMutation} from '@dms/api/customer';

import {debounce, Nullish} from 'shared/web';

import {COUNTRY_CZE} from '../constants/country';
import {handleApiError} from '../utils/handleApiError';
import {useTenant} from './useTenant';

const DEBOUNCE_TIMEOUT = 700;

type ParsePersonalIdFormApi = {
  getValues: (
    name:
      | 'foundingPerson.personData.personalIdentifier'
      | 'foundingPerson.personData.citizenshipCode'
  ) => string | Nullish;
  setValue: (
    name: 'foundingPerson.personData.birthdate' | 'foundingPerson.personData.genderKey',
    value: string | null
  ) => void;
};

export const useParsePersonalId = () => {
  const {tenantCountry, isTenantCountrySupported} = useTenant();

  const [parsePersonalId] = useParsePersonalIdMutation();

  const parsePersonalIdData = useCallback(
    (formApi: ParsePersonalIdFormApi) => {
      const personalId = formApi.getValues('foundingPerson.personData.personalIdentifier');
      let country = formApi.getValues('foundingPerson.personData.citizenshipCode');

      if (isTenantCountrySupported(COUNTRY_CZE)) {
        country = tenantCountry;
      }

      if (!personalId || !country) {
        return;
      }

      const args: ParsePersonalIdApiArg = {
        parsePersonalIdRequestBody: {
          personalId: replace('/', '')(personalId ?? ''),
          country,
        },
      };

      parsePersonalId(args)
        .unwrap()
        .then((personalIdInfo) => {
          formApi.setValue('foundingPerson.personData.birthdate', personalIdInfo.birthdate);
          formApi.setValue('foundingPerson.personData.genderKey', personalIdInfo.genderKey);
        })
        .catch((error: Error) => handleApiError(error, {silent: true}));
    },
    [isTenantCountrySupported, parsePersonalId, tenantCountry]
  );

  return debounce(parsePersonalIdData, DEBOUNCE_TIMEOUT);
};
